import { useEffect, useState } from 'react'
import { BellRinging, X } from '@phosphor-icons/react'
import { Button, notify } from '@/design-system'
import { useT } from '@/i18n'
import { staffPushService, type PushState } from '@/services/staffPushService'

const SNOOZE_KEY = 'aos.pushPrompt.snoozedUntil'
const SNOOZE_MS = 24 * 3600_000

const snoozed = () => {
  try { return Number(localStorage.getItem(SNOOZE_KEY) || 0) > Date.now() } catch { return false }
}

/**
 * «ابعت لكله يعمل allow notifications» (owner, 2026-10-01): every employee whose device does not get the alerts yet is
 * asked, once a day, to switch them on — one tap, because a browser only asks for notifications after a person taps.
 * Gone for good once they are on; a device that blocked them or cannot get them is not nagged.
 */
export function PushPrompt() {
  const { locale } = useT()
  const ar = locale === 'ar'
  const [state, setState] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)
  const [hidden, setHidden] = useState(snoozed)
  useEffect(() => { if (!hidden) void staffPushService.state().then(setState).catch(() => undefined) }, [hidden])
  if (hidden || state !== 'off') return null

  const later = () => {
    try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS)) } catch { /* the bar simply comes back */ }
    setHidden(true)
  }
  const enable = async () => {
    setBusy(true)
    try {
      const next = await staffPushService.enable()
      setState(next)
      if (next === 'on') notify.success(ar ? 'تم التفعيل — ستصلك التنبيهات على هذا الجهاز' : 'Alerts are on for this device')
      else if (next === 'denied') notify.error(new Error(ar ? 'رفض المتصفح الإذن — فعل الإشعارات من علامة القفل بجوار الرابط' : 'Blocked by the browser'))
    } catch (e) {
      notify.error(e)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div role="status" className="animate-slide-up flex flex-wrap items-center justify-center gap-3 border-b border-brand/30 bg-brand-soft px-4 py-2 text-sm">
      <BellRinging size={18} weight="fill" className="text-brand" aria-hidden />
      <span className="font-medium">{ar ? 'فعل الإشعارات على هذا الجهاز لتصلك تنبيهات العمل ورسائل التكاملات حتى لو كان البرنامج مغلقا.' : 'Turn on notifications for this device to get work alerts even when the app is closed.'}</span>
      <Button size="sm" variant="primary" loading={busy} onClick={() => void enable()}>{ar ? 'تفعيل الإشعارات' : 'Turn on'}</Button>
      <button type="button" onClick={later} className="inline-flex items-center gap-1 text-xs text-fg-muted hover:text-fg" aria-label={ar ? 'لاحقا' : 'Later'}>
        <X size={14} />{ar ? 'لاحقا' : 'Later'}
      </button>
    </div>
  )
}
